import type { CallEvaluationStatus } from '@vagusx/clients';

/**
 * The traffic-light tone for a call's status, in one place.
 *
 * Three surfaces render this signal — the status pill and the office-note box
 * in `CallEvaluationSection`, and the patient initials in `ActivityPage` — and
 * they must never disagree about what yellow means. Any new surface reads from
 * here rather than re-deriving the mapping.
 *
 * Colours are the design system's semantic tokens (`--color-success`,
 * `--color-warning`, `--color-destructive` in `index.css`), never raw values.
 * Those tokens carry separate oklch values for dark surfaces, so both themes
 * are handled without this file knowing about either.
 */
export interface StatusTone {
  /** Badge classes for the status pill. Overrides the shadcn variant via twMerge. */
  pill: string;
  /** Tinted panel for the office note — background + border. */
  box: string;
  /** Icon colour inside the office-note box. */
  boxIcon: string;
  /** Avatar tint for the patient initials: soft fill, ring, and readable text. */
  avatar: string;
  /**
   * Spoken/hover description. The avatar is otherwise colour-only, which would
   * make the tone invisible to a screen reader and to anyone who can't
   * distinguish the hues.
   */
  label: string;
}

const TONES: Record<CallEvaluationStatus, StatusTone> = {
  all_clear: {
    pill: 'bg-success/15 text-success border-success/30',
    box: 'bg-success/10 border-success/30',
    boxIcon: 'text-success',
    avatar: 'bg-success/10 ring-2 ring-success/30 text-success',
    label: 'Call status: all clear',
  },
  follow_up: {
    pill: 'bg-warning/15 text-warning border-warning/30',
    box: 'bg-warning/10 border-warning/30',
    boxIcon: 'text-warning',
    avatar: 'bg-warning/10 ring-2 ring-warning/30 text-warning',
    label: 'Call status: needs follow-up',
  },
  needs_you: {
    pill: 'bg-destructive/15 text-destructive border-destructive/30',
    box: 'bg-destructive/10 border-destructive/30',
    boxIcon: 'text-destructive',
    avatar: 'bg-destructive/10 ring-2 ring-destructive/30 text-destructive',
    label: 'Call status: needs you',
  },
};

/**
 * Tone for a status, or `null` when there is none.
 *
 * `null` is a real answer, not a gap: most historical calls and every
 * non-outbound call have no evaluation row, and a call we never judged must
 * render neutral rather than green. Callers fall back to their own muted
 * styling.
 */
export function statusTone(status: CallEvaluationStatus | null | undefined): StatusTone | null {
  return status ? (TONES[status] ?? null) : null;
}
